import { useState, useCallback } from 'react'
import type { SunoPromptResult as Result } from '@creanizer/types'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Separator } from '@creanizer/ui'
import { toast } from 'sonner'
import { Copy, Save, ChevronDown, ChevronUp, Music, FileText, RefreshCw } from 'lucide-react'
import { Checkbox } from '@creanizer/ui'

interface SunoPromptResultProps {
  result: Result
  onSave?: () => void
  batchResults?: Result[]
  onSelectBatchResult?: (result: Result) => void
  onRegenerateLyricsSection?: (sectionName: string) => void
  onRegenerateMultipleSections?: (sectionNames: string[]) => void
  isRegeneratePreview?: boolean
  onSaveRegeneratedResult?: () => void
  onRevertToOriginal?: () => void
  onUpdateLyrics?: (newLyrics: string) => void
}

export function SunoPromptResult({
  result,
  onSave,
  batchResults = [],
  onSelectBatchResult,
  onRegenerateLyricsSection,
  onRegenerateMultipleSections,
  isRegeneratePreview = false,
  onSaveRegeneratedResult,
  onRevertToOriginal,
  onUpdateLyrics,
}: SunoPromptResultProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    prompt: true,
  })
  const [selectedSections, setSelectedSections] = useState<number[]>([])
  const [isEditingLyrics, setIsEditingLyrics] = useState(false)
  const [editedLyrics, setEditedLyrics] = useState('')

  // 防御的チェック
  if (!result || !result.promptInfo || !result.mainPrompt) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        プロンプトデータが利用できません
      </div>
    )
  }

  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }))
  }, [])

  const copyToClipboard = useCallback(async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${label}をコピーしました`)
    } catch (error) {
      toast.error('コピーに失敗しました')
    }
  }, [])

  const handleStartEditLyrics = useCallback(() => {
    setEditedLyrics(result.lyricsWithTags?.fullLyrics || '')
    setIsEditingLyrics(true)
  }, [result.lyricsWithTags])

  const handleSaveLyrics = useCallback(() => {
    if (onUpdateLyrics) {
      onUpdateLyrics(editedLyrics)
      setIsEditingLyrics(false)
      toast.success('歌詞を更新しました')
    }
  }, [editedLyrics, onUpdateLyrics])

  const handleCancelEditLyrics = useCallback(() => {
    setIsEditingLyrics(false)
    setEditedLyrics('')
  }, [])

  const getQualityBadgeVariant = (quality: string) => {
    switch (quality) {
      case '高': return 'default'
      case '中': return 'secondary'
      case '低': return 'destructive'
      default: return 'outline'
    }
  }

  const getCharacterCountBadgeVariant = (count: number) => {
    if (count <= 700) return 'default'
    if (count <= 900) return 'secondary'
    return 'destructive'
  }

  return (
    <div className="space-y-4">
      {/* 再生成プレビュー通知 */}
      {isRegeneratePreview && (
        <Card className="border-orange-500 bg-orange-50">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 text-2xl">⚠️</div>
                <div className="flex-1">
                  <h3 className="font-semibold text-orange-900 mb-1">
                    再生成プレビュー中
                  </h3>
                  <p className="text-sm text-orange-800">
                    これは再生成された結果のプレビューです。気に入った場合は「この結果を保存」をクリックして保存してください。元に戻したい場合は「元に戻す」をクリックしてください。
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {onSaveRegeneratedResult && (
                  <Button
                    size="sm"
                    onClick={onSaveRegeneratedResult}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    この結果を保存
                  </Button>
                )}
                {onRevertToOriginal && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onRevertToOriginal}
                    className="border-orange-600 text-orange-600 hover:bg-orange-100"
                  >
                    元に戻す
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* バッチ結果セレクター */}
      {batchResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>バッチ生成結果</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-24">
              <div className="flex gap-2">
                {batchResults.map((batch, index) => (
                  <Button
                    key={index}
                    variant={batch === result ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onSelectBatchResult?.(batch)}
                  >
                    バリエーション {index + 1}
                  </Button>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* プロンプト情報 */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>{result.promptInfo?.title || 'タイトル未設定'}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {result.promptInfo?.summary || '概要がありません'}
              </p>
            </div>
            <div className="flex gap-2">
              <Badge variant={getQualityBadgeVariant(result.promptInfo?.estimatedQuality || '中')}>
                {result.promptInfo?.estimatedQuality || '中'}品質
              </Badge>
              <Badge variant="outline">
                {result.promptInfo?.type || 'instrumental'}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-4 text-sm">
              <span>ジャンル: <strong>{result.promptInfo?.genre || '未設定'}</strong></span>
              <span>推奨時間: <strong>{result.promptInfo?.targetLength || '2-3分'}</strong></span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm">文字数:</span>
              <Badge variant={getCharacterCountBadgeVariant(result.promptInfo?.characterCount || 500)}>
                {result.promptInfo?.characterCount || 500} / 1000
              </Badge>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => copyToClipboard(result.mainPrompt?.fullPrompt || '', 'メインプロンプト')}
            >
              <Copy className="w-4 h-4 mr-2" />
              Sunoにコピー
            </Button>
            {onSave && (
              <Button size="sm" variant="outline" onClick={onSave}>
                <Save className="w-4 h-4 mr-2" />
                保存
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* タブで詳細情報を表示 */}
      <Tabs defaultValue="prompt" className="w-full">
        <TabsList className={`grid w-full ${result.lyricsWithTags ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <TabsTrigger value="prompt">
            <Music className="w-4 h-4 mr-2" />
            プロンプト
          </TabsTrigger>
          {result.lyricsWithTags && (
            <TabsTrigger value="lyrics">
              <FileText className="w-4 h-4 mr-2" />
              歌詞
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="prompt">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>メインプロンプト</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleSection('prompt')}
                >
                  {expandedSections.prompt ? <ChevronUp /> : <ChevronDown />}
                </Button>
              </div>
            </CardHeader>
            {expandedSections.prompt && (
              <CardContent className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <pre className="whitespace-pre-wrap text-sm font-mono">
                    {result.mainPrompt?.fullPrompt || 'プロンプトデータがありません'}
                  </pre>
                </div>

                {result.mainPrompt?.keywords && result.mainPrompt.keywords.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">キーワード</h4>
                    <div className="flex flex-wrap gap-2">
                      {result.mainPrompt.keywords.map((keyword, index) => (
                        <Badge key={index} variant="secondary">
                          {keyword}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {result.mainPrompt?.emphasis?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">強調要素</h4>
                    <div className="flex flex-wrap gap-2">
                      {result.mainPrompt.emphasis.map((item, index) => (
                        <Badge key={index} variant="default">
                          {item}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {result.negativePrompt && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">ネガティブプロンプト</h4>
                    <div className="p-4 bg-destructive/10 rounded-lg">
                      <pre className="whitespace-pre-wrap text-sm font-mono">
                        {result.negativePrompt.prompt}
                      </pre>
                    </div>
                    {result.negativePrompt.reasons?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-muted-foreground">避ける理由:</p>
                        {result.negativePrompt.reasons.map((reason, index) => (
                          <div key={index} className="text-xs">
                            <span className="font-medium">{reason.element}:</span> {reason.reason}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        </TabsContent>

        {result.lyricsWithTags && (
          <TabsContent value="lyrics">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>歌詞構造</CardTitle>
                  {onUpdateLyrics && !isEditingLyrics && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleStartEditLyrics}
                    >
                      編集
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {isEditingLyrics ? (
                    <div className="space-y-2">
                      <textarea
                        className="w-full min-h-[300px] p-4 border rounded-lg font-mono text-sm bg-yellow-50 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        value={editedLyrics}
                        onChange={(e) => setEditedLyrics(e.target.value)}
                        placeholder="歌詞を入力..."
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveLyrics}
                        >
                          <Save className="w-4 h-4 mr-2" />
                          保存
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelEditLyrics}
                        >
                          キャンセル
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-muted rounded-lg">
                      <pre className="whitespace-pre-wrap text-sm">
                        {result.lyricsWithTags.fullLyrics}
                      </pre>
                    </div>
                  )}

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-semibold">セクション構成</h4>
                      {onRegenerateMultipleSections && selectedSections.length > 0 && (
                        <Button
                          size="sm"
                          onClick={() => {
                            // 選択されたインデックスから番号付きセクション名を生成
                            const sectionNames = selectedSections.map(idx => {
                              const section = result.lyricsWithTags?.structure?.[idx]
                              if (!section) return ''

                              // 同じセクション名の出現回数をカウント
                              const sameNameSections = result.lyricsWithTags.structure?.filter(s => s.section === section.section) || []
                              const sameNameCount = sameNameSections.length

                              if (sameNameCount <= 1) {
                                return section.section
                              }

                              // このインデックスが何番目の同名セクションか計算
                              const sameNameIndex = sameNameSections.findIndex((_, sIdx) => {
                                const actualIndex = result.lyricsWithTags.structure?.findIndex(s => s === sameNameSections[sIdx])
                                return actualIndex === idx
                              }) + 1

                              return `${section.section} (${sameNameIndex}つ目)`
                            }).filter(Boolean)

                            onRegenerateMultipleSections(sectionNames)
                            setSelectedSections([])
                          }}
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          選択したセクションを再生成 ({selectedSections.length})
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {result.lyricsWithTags.structure?.map((section, index) => {
                        // 同じセクション名の出現回数をカウント
                        const sameNameSections = result.lyricsWithTags.structure?.filter(s => s.section === section.section) || []
                        const sameNameCount = sameNameSections.length
                        const sameNameIndex = sameNameSections.findIndex((_, idx) => {
                          const actualIndex = result.lyricsWithTags.structure?.findIndex(s => s === sameNameSections[idx])
                          return actualIndex === index
                        }) + 1

                        // 同じ名前が複数ある場合は番号を表示
                        const displayName = sameNameCount > 1
                          ? `${section.section} (${sameNameIndex}つ目)`
                          : section.section

                        return (
                          <div key={index} className="flex justify-between items-center p-2 bg-secondary/20 rounded">
                            <div className="flex items-center gap-2">
                              {onRegenerateMultipleSections && (
                                <Checkbox
                                  checked={selectedSections.includes(index)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedSections([...selectedSections, index])
                                    } else {
                                      setSelectedSections(selectedSections.filter(i => i !== index))
                                    }
                                  }}
                                />
                              )}
                              <span className="font-medium">{displayName}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {section.duration && (
                                <Badge variant="outline">{section.duration}</Badge>
                              )}
                              {onRegenerateLyricsSection && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => onRegenerateLyricsSection(section.section)}
                                  title="このセクションを再生成"
                                >
                                  <RefreshCw className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span>総セクション数: {result.lyricsWithTags.totalSections || 0}</span>
                    <span>推定時間: {result.lyricsWithTags.estimatedDuration || 'N/A'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}